import { Card, CardBody, CardHeader, Chip, SortDescriptor } from '@nextui-org/react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '~/components/common/Button'
import { Column, DataTable } from '~/components/common/DataTable'
import UserDetails from '~/components/super-admin/UserDetails'
import { setImpersonationReturnUrl } from '~/contexts/ImpersonationContext'
import {
  UserListItem as ApiUserListItem,
  getAllUsers,
  startImpersonation
} from '~/services/superAdminService'
import { BetterToast } from '~/utils/toast'

// Extend with id field for DataTable compatibility
interface UserListItem extends ApiUserListItem {
  id: number
}

function UserManagement() {
  const [searchParams, setSearchParams] = useSearchParams()

  // Data state
  const [users, setUsers] = useState<UserListItem[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Table state - initialize from URL params if present
  const [filterValue, setFilterValue] = useState(() => searchParams.get('filter') || '')
  const [page, setPage] = useState(() => {
    const p = searchParams.get('page')
    return p ? parseInt(p, 10) : 1
  })
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const r = searchParams.get('rows')
    return r ? parseInt(r, 10) : 20
  })
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>(() => ({
    column: searchParams.get('sort') || 'created_time',
    direction: (searchParams.get('dir') as 'ascending' | 'descending') || 'descending'
  }))

  // User details modal state
  const [detailsUserId, setDetailsUserId] = useState<number | null>(null)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)

  const openDetailsModal = (user: UserListItem) => {
    setDetailsUserId(user.user_id)
    setIsDetailsOpen(true)
  }

  const closeDetailsModal = () => {
    setIsDetailsOpen(false)
    setDetailsUserId(null)
  }

  const columns: Column[] = [
    { name: 'User ID', uid: 'user_id', sortable: true },
    { name: 'Email', uid: 'email', sortable: true },
    { name: 'Name', uid: 'name', sortable: true },
    { name: 'Created', uid: 'created_time', sortable: true },
    { name: 'Email Verified', uid: 'email_verified', sortable: true },
    { name: 'Phone Verified', uid: 'phone_verified', sortable: true },
    { name: 'MFA', uid: 'mfa_enabled', sortable: true },
    { name: 'Actions', uid: 'actions' }
  ]

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await getAllUsers()
      // Map user_id to id for DataTable compatibility
      setUsers(data.map(user => ({ ...user, id: user.user_id })))
    } catch {
      BetterToast.error('Failed to fetch users')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // Sync state to URL query params
  useEffect(() => {
    const params = new URLSearchParams()

    // Only set non-default values
    if (filterValue) params.set('filter', filterValue)
    if (page !== 1) params.set('page', String(page))
    if (rowsPerPage !== 20) params.set('rows', String(rowsPerPage))
    if (sortDescriptor.column && sortDescriptor.column !== 'created_time') {
      params.set('sort', String(sortDescriptor.column))
    }
    if (sortDescriptor.direction && sortDescriptor.direction !== 'descending') {
      params.set('dir', sortDescriptor.direction)
    }

    // Update URL without navigation (replace to avoid polluting history)
    setSearchParams(params, { replace: true })
  }, [filterValue, page, rowsPerPage, sortDescriptor, setSearchParams])

  const filteredItems = useMemo(() => {
    if (!filterValue) return users
    const lower = filterValue.toLowerCase()
    return users.filter(
      user =>
        user.email.toLowerCase().includes(lower) ||
        (user.name && user.name.toLowerCase().includes(lower)) ||
        user.user_id.toString().includes(lower)
    )
  }, [users, filterValue])

  const sortedItems = useMemo(() => {
    if (!sortDescriptor.column) return filteredItems
    return [...filteredItems].sort((a, b) => {
      const col = sortDescriptor.column as keyof UserListItem
      const first = a[col]
      const second = b[col]

      // Handle null/undefined
      if (first === null || first === undefined)
        return sortDescriptor.direction === 'ascending' ? 1 : -1
      if (second === null || second === undefined)
        return sortDescriptor.direction === 'ascending' ? -1 : 1

      // Compare
      let cmp = 0
      if (typeof first === 'string' && typeof second === 'string') {
        cmp = first.localeCompare(second)
      } else if (typeof first === 'boolean' && typeof second === 'boolean') {
        cmp = first === second ? 0 : first ? -1 : 1
      } else if (typeof first === 'number' && typeof second === 'number') {
        cmp = first - second
      }

      return sortDescriptor.direction === 'ascending' ? cmp : -cmp
    })
  }, [filteredItems, sortDescriptor])

  const pages = Math.max(1, Math.ceil(sortedItems.length / rowsPerPage))

  // Clamp page to valid range after data has loaded
  // This ensures page doesn't exceed total pages after filtering
  // Only clamp when we have actual data (users.length > 0) to preserve initial URL page
  useEffect(() => {
    if (users.length > 0 && page > pages) {
      setPage(pages)
    }
  }, [users.length, page, pages])

  const items = useMemo(() => {
    const start = (page - 1) * rowsPerPage
    const end = start + rowsPerPage
    return sortedItems.slice(start, end)
  }, [page, sortedItems, rowsPerPage])

  const handleUpdate = () => {
    fetchUsers()
  }

  const [impersonatingUserId, setImpersonatingUserId] = useState<number | null>(null)

  const handleImpersonate = async (user: UserListItem) => {
    try {
      setImpersonatingUserId(user.user_id)

      // Get current URL to return to after impersonation ends
      const currentPath = window.location.pathname + window.location.search

      // Save locally as backup (in case cookie doesn't work)
      setImpersonationReturnUrl(currentPath)

      // Pass return URL to backend so it's saved in cookie (for logout redirect)
      const info = await startImpersonation({
        user_id: user.user_id,
        return_url: currentPath
      })
      BetterToast.success(`Now viewing as ${info.status.impersonated_user_email} (read-only mode)`)

      // Navigate to the user's first workspace if they have one
      if (info.workspaces.length > 0) {
        // Use /w/ route for workspace, not /c/ which is for conversations
        window.location.href = `/w/${info.workspaces[0].workspace_uuid}`
      } else {
        // No workspaces, just reload to show impersonation banner
        window.location.href = '/'
      }
    } catch (error: unknown) {
      BetterToast.error((error as Error).message || 'Failed to start impersonation')
    } finally {
      setImpersonatingUserId(null)
    }
  }

  const renderCell = (item: UserListItem, columnKey: string) => {
    switch (columnKey) {
      case 'user_id':
        return <span className='font-mono text-sm'>#{item.user_id}</span>
      case 'email':
        return <span className='text-sm'>{item.email}</span>
      case 'name':
        return <span className='text-sm text-gray-600'>{item.name || '-'}</span>
      case 'created_time':
        return (
          <span className='text-sm text-gray-500'>
            {item.created_time ? new Date(item.created_time).toLocaleDateString() : '-'}
          </span>
        )
      case 'email_verified':
        return (
          <Chip color={item.email_verified ? 'success' : 'warning'} variant='flat' size='sm'>
            {item.email_verified ? 'Yes' : 'No'}
          </Chip>
        )
      case 'phone_verified':
        return (
          <Chip
            color={item.phone_verified ? 'success' : item.phone_number ? 'warning' : 'default'}
            variant='flat'
            size='sm'
          >
            {item.phone_verified ? 'Yes' : item.phone_number ? 'No' : 'N/A'}
          </Chip>
        )
      case 'mfa_enabled':
        return (
          <Chip color={item.mfa_enabled ? 'success' : 'default'} variant='flat' size='sm'>
            {item.mfa_enabled ? 'Yes' : 'No'}
          </Chip>
        )
      case 'actions':
        return (
          <div className='flex gap-2'>
            <Button
              variant='primary'
              className='h-7 text-xs px-2'
              onClick={() => openDetailsModal(item)}
            >
              View Details
            </Button>
            <Button
              variant='outlined'
              className='h-7 text-xs px-2'
              onClick={() => handleImpersonate(item)}
              isDisabled={impersonatingUserId === item.user_id}
            >
              {impersonatingUserId === item.user_id ? '...' : 'Impersonate'}
            </Button>
          </div>
        )
      default:
        return null
    }
  }

  return (
    <Card className='w-full'>
      <CardHeader className='flex justify-between items-center'>
        <h2 className='text-xl font-bold'>User Management</h2>
        <Button variant='outlined' className='h-8' onClick={fetchUsers} isDisabled={isLoading}>
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </Button>
      </CardHeader>
      <CardBody>
        <DataTable
          items={items}
          columns={columns}
          page={page}
          pages={pages}
          rowsPerPage={rowsPerPage}
          filterValue={filterValue}
          sortDescriptor={sortDescriptor}
          isLoading={isLoading}
          onSearchChange={value => {
            setFilterValue(value)
            setPage(1)
          }}
          onClear={() => {
            setFilterValue('')
            setPage(1)
          }}
          onRowsPerPageChange={setRowsPerPage}
          onPageChange={setPage}
          onSortChange={setSortDescriptor}
          renderCell={renderCell}
          searchPlaceholder='Filter by email, name, or user ID...'
          totalCount={filteredItems.length}
        />
      </CardBody>

      {/* User Details Modal */}
      {detailsUserId && (
        <UserDetails
          userId={detailsUserId}
          isOpen={isDetailsOpen}
          onClose={closeDetailsModal}
          onUpdate={handleUpdate}
        />
      )}
    </Card>
  )
}

export default UserManagement
